import { Injectable, isDevMode } from '@angular/core';
import { Observable, ReplaySubject, defer, from, throwError } from 'rxjs';
import { AuthCredentialLockService } from './auth-credential-lock.service';
import { AuthSessionStateService } from './auth-session-state.service';
import { isTerminalAuthFailure, serverRetryAtOf } from '../utils/http-status.util';

export type RefreshSource = 'timer' | 'interceptor';

/** Thrown instead of calling the server while a cooldown from an earlier failure is still running. */
export class RefreshCooldownError extends Error {
  constructor(public readonly retryAt: number, public readonly lastFailure?: unknown) {
    super(`Token refresh is cooling down until ${new Date(retryAt).toISOString()}`);
    this.name = 'RefreshCooldownError';
  }
}

/** Thrown when a request belonging to a replaced login tries to continue. */
export class StaleSessionError extends Error {
  constructor() {
    super('The session this request belongs to is no longer current');
    this.name = 'StaleSessionError';
  }
}

interface RefreshAttempt {
  readonly sessionId: string | null;
  readonly subject: ReplaySubject<void>;
  settled: boolean;
}

/**
 * Owns token refresh: one call at a time across every tab, one place that records what happened, and
 * one cooldown that the timer and the interceptor both obey.
 *
 * The previous design gave each caller its own copy of the work — the timer and the interceptor each
 * started refreshes, each recorded their own metrics, and each decided independently what a failure
 * meant. That is how a single refresh could be followed by a `failRefresh()` for a request error that
 * had nothing to do with it.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshCoordinator {
  /** Supplied by AuthService at construction to avoid a circular dependency. */
  private performRefresh: (() => Observable<void>) | null = null;
  private onRefreshSucceeded: (() => void) | null = null;

  private attempt: RefreshAttempt | null = null;
  private lastRefreshTime: number | null = null;
  private refreshSource: RefreshSource | null = null;

  private nextAllowedAt = 0;
  private backoffMs = 0;
  private lastFailure: unknown = null;

  private readonly INITIAL_BACKOFF_MS = 1000;
  private readonly MAX_BACKOFF_MS = 60_000;

  constructor(
    private readonly lock: AuthCredentialLockService,
    private readonly sessionState: AuthSessionStateService,
  ) {}

  /**
   * Registers how a refresh is actually performed. AuthService calls this once; keeping the HTTP call
   * behind a callback is what lets this service sit below AuthService without a DI cycle.
   */
  registerRefreshHandler(perform: () => Observable<void>, onSuccess?: () => void): void {
    this.performRefresh = perform;
    this.onRefreshSucceeded = onSuccess ?? null;
  }

  isRefreshInProgress(): boolean {
    return this.attempt !== null && !this.attempt.settled;
  }

  getLastRefreshTime(): number | null {
    return this.lastRefreshTime;
  }

  getRefreshSource(): RefreshSource | null {
    return this.refreshSource;
  }

  /** The moment a refusal from cooldown says we may ask again, or 0 when not cooling down. */
  getRetryAt(): number {
    return this.nextAllowedAt > Date.now() ? this.nextAllowedAt : 0;
  }

  revisionAdvancedSince(sessionId: string | null | undefined, revision: number): boolean {
    return this.sessionState.revisionAdvancedSince(sessionId, revision);
  }

  currentRevision(): number {
    return this.sessionState.credentialRevision;
  }

  currentSessionId(): string | null {
    return this.sessionState.sessionId;
  }

  /**
   * The shared refresh. Every caller gets the same in-flight attempt; the attempt itself is owned
   * here and is not cancelled when a caller unsubscribes.
   *
   * Cancellation safety matters more than it looks. Callers subscribe through `switchMap`s that get
   * torn down all the time — a component is destroyed, a newer request supersedes an older one — and
   * if the HTTP call were the caller's subscription, an ordinary teardown would abort a rotation the
   * server has already applied, leaving the browser holding a token that is about to be revoked.
   */
  getOrStart(source: RefreshSource, sessionId: string | null): Observable<void> {
    const existing = this.attempt;
    if (existing && !existing.settled) {
      // A refresh for a different login is not ours to wait for.
      if (existing.sessionId !== sessionId) {
        return throwError(() => new StaleSessionError());
      }
      return existing.subject.asObservable();
    }

    const now = Date.now();
    if (this.nextAllowedAt > now) {
      // The timer's backoff and the interceptor's retries are the same budget. Without this, a page
      // making requests during an outage would hammer /auth/refresh straight past the timer's waiting.
      return throwError(() => new RefreshCooldownError(this.nextAllowedAt, this.lastFailure));
    }

    if (!this.performRefresh) {
      return throwError(() => new Error('TokenRefreshCoordinator has no refresh handler registered'));
    }

    return this.startAttempt(source, sessionId);
  }

  /**
   * Discards any in-flight attempt and clears the cooldown. Used when a session ends, so the next
   * login starts from a clean slate rather than inheriting the dead session's backoff.
   */
  reset(): void {
    if (this.attempt && !this.attempt.settled) {
      this.attempt.settled = true;
      this.attempt.subject.error(new StaleSessionError());
    }
    this.attempt = null;
    this.lastRefreshTime = null;
    this.refreshSource = null;
    this.nextAllowedAt = 0;
    this.backoffMs = 0;
    this.lastFailure = null;
  }

  private startAttempt(source: RefreshSource, sessionId: string | null): Observable<void> {
    // ReplaySubject, not shareReplay: RxJS 7's shareReplay resets the shared source on error, so a
    // caller that subscribed after a failed attempt would silently start a *second* HTTP request
    // instead of being told what already went wrong.
    const subject = new ReplaySubject<void>(1);
    const attempt: RefreshAttempt = { sessionId, subject, settled: false };
    this.attempt = attempt;
    this.refreshSource = source;

    this.log(`[TokenRefreshCoordinator] Starting refresh from ${source}`);

    from(this.runLocked(sessionId)).subscribe({
      next: () => {
        attempt.settled = true;
        this.lastRefreshTime = Date.now();
        this.backoffMs = 0;
        this.nextAllowedAt = 0;
        this.lastFailure = null;
        this.onRefreshSucceeded?.();
        subject.next();
        subject.complete();
      },
      error: (error) => {
        attempt.settled = true;
        this.noteFailure(error);
        subject.error(error);
      },
    });

    return subject.asObservable();
  }

  /**
   * Inside the lock, check the world again before spending a request on it.
   *
   * Two things can have happened while we queued: the login was replaced (drop this attempt), or
   * another tab refreshed the same login (adopt its result — the cookies are already in the jar we
   * share, so asking the server again would rotate a perfectly good token for nothing).
   */
  private async runLocked(sessionId: string | null): Promise<void> {
    const revisionAtQueueTime = this.sessionState.credentialRevision;

    return this.lock.runExclusive('refresh', async () => {
      if (sessionId && !this.sessionState.isCurrent(sessionId)) {
        this.log('[TokenRefreshCoordinator] Session replaced while queued; abandoning refresh');
        throw new StaleSessionError();
      }

      if (this.sessionState.revisionAdvancedSince(sessionId, revisionAtQueueTime)) {
        this.log('[TokenRefreshCoordinator] Another tab refreshed while we queued; adopting its result');
        return;
      }

      await new Promise<void>((resolve, reject) => {
        defer(() => this.performRefresh!()).subscribe({
          next: () => undefined,
          error: reject,
          complete: resolve,
        });
      });
    });
  }

  private noteFailure(error: unknown): void {
    this.lastFailure = error;

    if (error instanceof StaleSessionError) {
      return; // Not a failure of the server; nothing to back off from.
    }

    if (isTerminalAuthFailure(error)) {
      // The credential was rejected. Retrying cannot help, and the session is ending anyway.
      this.nextAllowedAt = 0;
      this.backoffMs = 0;
      return;
    }

    this.backoffMs = this.backoffMs === 0
      ? this.INITIAL_BACKOFF_MS
      : Math.min(this.backoffMs * 2, this.MAX_BACKOFF_MS);

    // The server's own instruction wins when it asked us to wait longer than our backoff would.
    const serverRetryAt = serverRetryAtOf(error) ?? 0;
    this.nextAllowedAt = Math.max(Date.now() + this.backoffMs, serverRetryAt);

    this.log(
      `[TokenRefreshCoordinator] Refresh failed; next attempt allowed at ` +
      `${new Date(this.nextAllowedAt).toISOString()}`);
  }

  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }
}
